'use client';

import React from 'react';
import { DateTime } from 'luxon';
import { BasicNewsManager, BreakingNews } from '@benzinga/basic-news-manager';
import { SessionContext } from '@benzinga/session-context';
import { useUser } from '@benzinga/user-context';
import { BreakingNewsBanner } from '../BreakingNewsBanner';
import { EmailVerificationBanner } from '../EmailVerificationBanner';
import { AdHeaderBanner } from '../Header/AdHeaderBanner';
import { OptimizelyTopBanner } from '../Header/RotatingBannerFallback';
import { useRevealOnFirstScroll } from '../../hooks/useRevealOnFirstScroll';
import { shouldLoadHeaderDisplayAd } from '../../utils/estimateDesktopStickyNavOffset';

export interface NavBannersProps {
  breakingNews?: BreakingNews[];
  hideBanner?: boolean;
  showHeaderDisplayAd?: boolean;
  expandedHeaderDisplayAd?: boolean;
  showRotatingBanner?: boolean;
  children?: React.ReactNode;
}

export const NavBanners = ({
  breakingNews: initialBreakingNews,
  hideBanner,
  showHeaderDisplayAd,
  expandedHeaderDisplayAd,
  showRotatingBanner,
  children,
}: NavBannersProps) => {
  const session = React.useContext(SessionContext);
  const user = useUser();

  const [breakingNews, setBreakingNews] = React.useState<BreakingNews[]>(initialBreakingNews ?? []);
  const [isBannerShowing, setIsBannerShowing] = React.useState<boolean>(!!initialBreakingNews?.length);
  const [viewport, setViewport] = React.useState<'mobile' | 'desktop' | 'pending'>('pending');

  const showEmailVerificationBanner = Boolean(user?.email && user.emailVerified === false);
  const shouldShowHeaderDisplayAd = !!showHeaderDisplayAd;

  React.useLayoutEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 799px)');
    const syncViewport = () => setViewport(mediaQuery.matches ? 'mobile' : 'desktop');
    syncViewport();
    mediaQuery.addEventListener('change', syncViewport);
    return () => mediaQuery.removeEventListener('change', syncViewport);
  }, []);

  React.useEffect(() => {
    if (hideBanner) return;

    const now = DateTime.now().setZone('America/New_York');
    const stillLive = (items: BreakingNews[]): BreakingNews[] =>
      Array.isArray(items)
        ? items.filter(item => now < DateTime.fromSeconds(Number(item.time_end)))
        : [];

    if (initialBreakingNews) {
      const live = stillLive(initialBreakingNews);
      if (live.length > 0) {
        setBreakingNews(live);
        setIsBannerShowing(true);
        return;
      }
    }

    const events = ['breaking', 'notice', 'event', 'news'];
    session
      .getManager(BasicNewsManager)
      .getBreakingNews()
      .then(res => {
        const result = res?.ok?.data;
        if (!result || result.length === 0) return;
        const next = result.filter(item => events.includes(item.type.toLowerCase()));
        if (next.length > 0) {
          setBreakingNews(next);
          setIsBannerShowing(true);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialBreakingNews, hideBanner]);

  const showBreakingNews = isBannerShowing && !hideBanner && breakingNews.length > 0;
  const breakingNewsRevealed = useRevealOnFirstScroll(showBreakingNews);
  const emailStripRevealed = useRevealOnFirstScroll(showEmailVerificationBanner);
  const loadAd = shouldLoadHeaderDisplayAd(viewport, shouldShowHeaderDisplayAd);

  return (
    <>
      {showRotatingBanner ? <OptimizelyTopBanner /> : null}

      {shouldShowHeaderDisplayAd ? (
        <AdHeaderBanner
          expandedHeaderDisplayAd={expandedHeaderDisplayAd}
          headerType={viewport === 'mobile' ? 'mobile' : 'desktop'}
          loadAd={loadAd}
        />
      ) : null}

      {children}

      {breakingNewsRevealed ? <BreakingNewsBanner data={breakingNews} /> : null}

      {emailStripRevealed && user ? <EmailVerificationBanner email={user.email} /> : null}
    </>
  );
};
